import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Globe, Calendar, BookOpen, Pencil, Loader } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useProfile } from '../hooks/useProfile';
import { useFollows } from '../hooks/useFollows';
import BookIdeaCard from '../components/BookIdeaCard';
import { useAuth } from '../hooks/useAuth';
import FollowButton from '../components/FollowButton';
import SocialLinks from '../components/SocialLinks';
import SlugRedirect from '../components/SlugRedirect';
import SocialShare from '../components/SocialShare';

export default function Profile() {
  return (
    <SlugRedirect type="profile">
      <ProfileContent />
    </SlugRedirect>
  );
}

function ProfileContent({ id = '' }: { id?: string }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, loading, error } = useProfile(id);
  const isOwnProfile = user?.id === id;
  const { isFollowing, followersCount, followingCount, loading: followLoading, toggleFollow } = useFollows(id);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Loader className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-red-50 border border-red-200 rounded-lg text-red-700">
        {error || 'Profile not found'}
      </div>
    );
  }

  const shareUrl = `${window.location.origin}/profile/${profile.username}`;
  const shareTitle = `Check out ${profile.username}'s profile on StoryMuse`;
  const shareDescription = profile.bio || `Follow ${profile.username} on StoryMuse to discover their stories`;

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Profile Header with Background */}
      <div className="relative">
        {profile.background_image_url ? (
          <div className="h-64 w-full overflow-hidden">
            <img
              src={profile.background_image_url}
              alt="Profile background"
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="h-64 w-full" />
        )}
        
        {/* Profile Info (Overlapping the background) */}
        <div className="absolute bottom-0 left-0 right-0 transform translate-y-1/2">
          <div className="mx-auto max-w-4xl px-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  {profile.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt={profile.username}
                      className="w-20 h-20 rounded-full object-cover ring-4 ring-white"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center ring-4 ring-white">
                      <User className="w-10 h-10 text-indigo-600" />
                    </div>
                  )}
                  
                  <div>
                    {profile.full_name && (
                      <h1 className="text-2xl font-bold text-gray-900 mb-1">{profile.full_name}</h1>
                    )}
                    <p className="text-lg text-gray-600">@{profile.username}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className={`px-3 py-1 text-sm rounded-full ${
                        profile.role === 'author'
                          ? 'bg-indigo-100 text-indigo-700'
                          : profile.role === 'admin'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
                      </span>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{followersCount} followers</span>
                        <span>{followingCount} following</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Joined {formatDistanceToNow(new Date(profile.created_at), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {!isOwnProfile && !followLoading && (
                    <FollowButton
                      isFollowing={isFollowing}
                      onToggle={toggleFollow}
                      className="mr-2"
                    />
                  )}
                  {isOwnProfile ? (
                    <Link
                      to="/settings"
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      <Pencil className="w-4 h-4" />
                      Edit Profile
                    </Link>
                  ) : (
                    <SocialShare
                      url={shareUrl}
                      title={shareTitle}
                      description={shareDescription}
                    />
                  )}
                </div>
              </div>

              {profile.bio && (
                <p className="text-gray-700 mt-6 whitespace-pre-wrap">{profile.bio}</p>
              )}

              {profile.website && (
                <a
                  href={profile.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 mt-4"
                >
                  <Globe className="w-4 h-4" />
                  {profile.website}
                </a>
              )}

              {profile.social_links && Object.keys(profile.social_links).length > 0 && (
                <div className="mt-4">
                  <SocialLinks links={profile.social_links} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Spacer for profile info overlap */}
      <div className="h-32" />

      {/* Book Ideas Section */}
      {profile.book_ideas && profile.book_ideas.length > 0 && (
        <div className="space-y-6 mt-16">
          <div className="flex items-center justify-between">
          
            {isOwnProfile && profile.role === 'author' && (
              <Link
                to="/new-idea"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Share New Idea
              </Link>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {profile.book_ideas.map((idea) => (
              <BookIdeaCard key={idea.id} idea={idea} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}